/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useState, useEffect } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { Divider, Icon, IconNode } from '@adobe-commerce/elsie/components';
import { Add, Minus } from '@adobe-commerce/elsie/icons';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@adobe-commerce/elsie/components/Accordion/Accordion.css';
export interface AccordionSectionProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'icon'> {
  defaultOpen?: boolean;
  actionIconPosition?: 'left' | 'right';
  ariaLabelTitle: string;
  iconOpen?: IconNode;
  iconClose?: IconNode;
  iconLeft?: IconNode;
  showIconLeft?: boolean;
  secondaryText?: string | VNode<HTMLAttributes<HTMLSpanElement>>;
  renderContentWhenClosed?: boolean;
  onStateChange?: (open: boolean) => void;
}

export const AccordionSection: FunctionComponent<AccordionSectionProps> = ({
  className,
  children,
  title,
  ariaLabelTitle,
  secondaryText,
  actionIconPosition = 'left',
  iconOpen = Add,
  iconClose = Minus,
  iconLeft = Add,
  showIconLeft = false,
  renderContentWhenClosed = true,
  defaultOpen,
  onStateChange,
  ...props
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const onClickHandler = (e: { stopImmediatePropagation: () => void }) => {
    e.stopImmediatePropagation();

    const next = !open;
    setOpen(next);
    onStateChange?.(next);
  };

  useEffect(() => {
    if (typeof defaultOpen !== 'undefined') {
      setOpen(defaultOpen);
    }
  }, [defaultOpen]);

  const buttonLabel = useText(
    `Dropin.Accordion.${open ? 'close' : 'open'}.label`
  ).label;

  const openIcon = (
    <Icon
      source={iconOpen}
      size="24"
      onClick={onClickHandler}
      onKeyPress={onClickHandler}
      className={'dropin-accordion-section__open-icon'}
    />
  );

  const closeIcon = (
    <Icon
      source={iconClose}
      size="24"
      onClick={onClickHandler}
      onKeyPress={onClickHandler}
      className={'dropin-accordion-section__close-icon'}
    />
  );

  const leftIcon = <Icon source={iconLeft} size="24" />;

  return (
    <div
      {...props}
      className={classes(['dropin-accordion-section', className])}
    >
      <div className={'dropin-accordion-section__heading'}>
        <div
          className={'dropin-accordion-section__flex'}
          onClick={onClickHandler}
          onKeyPress={onClickHandler}
          role="button"
          aria-label={`${buttonLabel} ${ariaLabelTitle ?? title}`}
          tabIndex={0}
        >
          <div className={'dropin-accordion-section__title-container'}>
            {actionIconPosition === 'left' && (open ? closeIcon : openIcon)}

            {showIconLeft && leftIcon}

            <h3 className={'dropin-accordion-section__title'}>{title}</h3>
          </div>
        </div>

        <div className={'dropin-accordion-section__secondary-text-container'}>
          {secondaryText && (
            <h4 className={'dropin-accordion-section__secondary-text'}>
              {secondaryText}
            </h4>
          )}

          {actionIconPosition === 'right' && (open ? closeIcon : openIcon)}
        </div>
      </div>

      <div
        className={'dropin-accordion-section__content-container'}
        style={{ display: open ? 'grid' : 'none' }}
      >
        {(open || (renderContentWhenClosed && !open)) && children}
      </div>
    </div>
  );
};

export interface AccordionProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'icon'> {
  actionIconPosition?: 'left' | 'right';
  iconOpen?: IconNode;
  iconClose?: IconNode;
  iconLeft?: IconNode;
  showIconLeft?: boolean;
  secondaryText?: string | VNode<HTMLAttributes<HTMLSpanElement>>;
  children: VNode<AccordionSectionProps>[] | VNode<AccordionSectionProps>;
}

export const Accordion: FunctionComponent<AccordionProps> = ({
  className,
  children,
  actionIconPosition = 'left',
  iconOpen = Add,
  iconClose = Minus,
  ...props
}) => {
  const divider = <Divider variant="secondary" />;

  const renderAccordionSection = (child: VNode<AccordionSectionProps>) => (
    <>
      <AccordionSection
        {...child.props}
        actionIconPosition={actionIconPosition}
        iconOpen={iconOpen}
        iconClose={iconClose}
      />
      {divider}
    </>
  );

  return (
    <div {...props} className={classes(['dropin-accordion', className])}>
      {[
        divider,
        ...(
          (Array.isArray(children)
            ? children
            : [children]) as VNode<AccordionSectionProps>[]
        ).map(renderAccordionSection),
      ]}
    </div>
  );
};
